import { useCallback, useContext, useEffect, useState } from 'react'
import GeoContext from 'contexts/geoContext'
import useTabVisibility from 'hooks/useTabVisibility'
import { multiCartRefreshFlightItemEvent } from 'analytics/snowplow/events'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { refreshMultiCartFlightItem } from './refreshMultiCartFlightItem'
import { CHARTER } from 'constants/provider'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { CHECKOUT_UPDATE_ITEM } from 'actions/actionConstants'

type FlightDataStatus = 'valid' | 'expired' | 'refreshing' | 'refreshFailed'

// setTimeout stores its delay as a 32-bit signed int; values above
// 2^31-1 overflow to 0 and fire immediately. Clamp to the max safe value.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

function isFlightExpired(expiry: number | undefined) {
  return !!expiry && Date.now() >= expiry
}

interface UseRefreshMultiCartFlightItemOptions {
  /**
   * Whether this flight is eligible for Maldives bundle pricing (no 15% markup).
   * When true, refresh will use source='charter' to get bundle pricing.
   * When false/undefined, refresh will use default pricing (with 15% markup for charter flights).
   */
  isBundleEligible?: boolean
}

function useRefreshMultiCartFlightItem(
  multiCartItem: App.MultiCartFlightItem,
  options: UseRefreshMultiCartFlightItemOptions = {},
): {
  dataStatus: FlightDataStatus
  initiateRefresh: () => void
  refreshStartedAt: number
} {
  const { isBundleEligible = false } = options
  const item = multiCartItem.item as App.Checkout.FlightItem
  const { expiry } = item
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)

  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const [dataStatus, setStatus] = useState<FlightDataStatus>(() => isFlightExpired(expiry) ? 'expired' : 'valid')
  const [refreshStartedAt, setRefreshStartedAt] = useState(0)

  const tabIsVisible = useTabVisibility()

  const enableSabreContent = useOptimizelyExperiment(OptimizelyFeatureFlags.flightsEnableSabreContent)
  const email = useAppSelector(state => state.auth.account.email)

  useEffect(() => {
    if (expiry && dataStatus === 'valid' && tabIsVisible) {
      const millisecondsUntilExpiry = expiry - Date.now()

      if (millisecondsUntilExpiry > 0) {
        const timeout = setTimeout(() => {
          setStatus('expired')
        }, Math.min(millisecondsUntilExpiry, MAX_TIMEOUT_MS))
        return () => clearTimeout(timeout)
      } else {
        setStatus('expired')
      }
    }
  }, [expiry, dataStatus, tabIsVisible])

  const initiateRefresh = useCallback(async() => {
    const { flights, occupants } = item

    if (!flights?.length || !occupants) {
      return
    }

    multiCartRefreshFlightItemEvent(item)
    setStatus('refreshing')
    setRefreshStartedAt(Date.now())

    try {
      // For charter flights, determine pricing context based on bundle eligibility
      // - isBundleEligible=true: use source='charter' for no 15% markup (bundle pricing)
      // - isBundleEligible=false: use source=undefined for 15% markup (standalone pricing)
      const isCharterFlight = item.flights?.[0]?.provider === CHARTER
      const shouldUseBundlePricing = isCharterFlight && isBundleEligible

      const result = await refreshMultiCartFlightItem({
        multiCartItem,
        source: shouldUseBundlePricing ? CHARTER : undefined,
        currentCurrency,
        currentRegionCode,
        enableSabreContent,
        useCharterDateMatching: isCharterFlight, // Use date matching for charter flights
        email,
      })

      // Update XState with the refreshed multi-cart item
      if (activeCartId) {
        multiCartActorRef.send({
          type: 'update cart item',
          cartId: activeCartId,
          multiCartItem: result.updatedMultiCartItem,
        })
      }

      // Also update Redux checkout.cart.items to keep them in sync
      // Note: Only dispatch if item already exists in Redux (updateCheckoutItem adds new items if not found)
      const updatedFlightItem = result.updatedMultiCartItem.item as App.Checkout.FlightItem
      dispatch((reduxDispatch, getState) => {
        const checkoutItems = getState().checkout.cart.items
        const existsInRedux = checkoutItems.some(i => i.itemId === updatedFlightItem.itemId)
        if (existsInRedux) {
          reduxDispatch({ type: CHECKOUT_UPDATE_ITEM, item: updatedFlightItem })
        }
      })

      setStatus('valid')
    } catch {
      // log error
      multiCartRefreshFlightItemEvent(item, 'failed')
      setStatus('refreshFailed')
      return
    }
  }, [multiCartActorRef, activeCartId, currentCurrency, currentRegionCode, item, multiCartItem, enableSabreContent, isBundleEligible, dispatch, email])

  return {
    dataStatus,
    initiateRefresh,
    refreshStartedAt,
  }
}

export default useRefreshMultiCartFlightItem
